import { TreeItems } from 'dnd-kit-sortable-tree';
import { QueryFactory } from 'services/api';
import {
  CreateDocumentDto,
  CreateTemplateDto,
  DocumentExportFormat,
  ProjectDocument,
  ProjectElement,
  Status,
} from 'services/api/api-client';
import {
  replaceItemById,
  tryInsertInSpecificFolder,
} from './documentsObserver';
import { BaseNode, NodeType } from './types/base';
import { triggerDownloadFileDialog } from 'helpers/file-helper';
import { getFileExtension } from 'pages/authorized/documents/components/document-exporting';
import {
  showErrorToast,
  showWarningToast,
} from 'components/uikit/toast/showToast';
import i18next from 'i18next';
import {
  getToastClassNameFunc,
  ToastStyle,
} from 'components/uikit/toast/ToastContainer';

export type DocumentData = {
  name: string;
  content: string;
};

export async function createDocuments(
  files: DocumentData[],
  revisionId: number,
  documentDtoCreateAction: (file: DocumentData) => CreateDocumentDto,
  onDocumentCreated?: (props?: any) => void | Promise<void>,
  onAllDocumentsCreated?: (props?: any) => void | Promise<void>,
  folderId?: string,
) {
  try {
    let folders = (await QueryFactory.RevisionClient.get(revisionId)).folders;
    const createDocumentDtos: CreateDocumentDto[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      createDocumentDtos.push(documentDtoCreateAction(file));
    }

    for (const documentDto of createDocumentDtos) {
      const document = await QueryFactory.DocumentClient.deferCreate(
        documentDto,
      );

      if (documentDto.name !== document.name)
        showWarningToast({
          content: {
            description: i18next.t(
              'Page.Projects.ProjectDetailsPage.FoldersTree.Resolved_Name_Conflict',
              {
                oldName: documentDto.name,
                newName: document.name,
              },
            ),
          },
          options: {
            className: getToastClassNameFunc(ToastStyle.Wide),
          },
        });

      const documentNode = createProjectDocument(
        String(document.id),
        document.name,
        document.id,
      );

      if (folderId) {
        tryInsertInSpecificFolder(folders, folderId, documentNode);
      } else {
        folders.push(documentNode);
      }
      folders = getCopiedItems(folders);
      onDocumentCreated?.({
        id: document.id,
        name: document.name,
        content: document.content,
      });
    }

    await QueryFactory.RevisionClient.patch(revisionId, {
      folders,
    });
    await onAllDocumentsCreated?.();
  } catch (e) {
    showErrorToast(e);
  }
}

export async function createTemplates(
  files: DocumentData[],
  onTemplateCreated?: () => void | Promise<void>,
  folderId?: string,
  newVersionInitialId?: number,
  comment?: string,
) {
  const templateDtoCreateAction = (file: DocumentData): CreateTemplateDto => {
    return {
      name: file.name,
      content: file.content,
      isImported: true,
      folderId: null,
      initialId: newVersionInitialId ?? null,
      creationComment: comment ?? null,
    };
  };

  try {
    let folders = await QueryFactory.TemplateClient.getFolders();

    const createTemplateDtos: CreateTemplateDto[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      createTemplateDtos.push(templateDtoCreateAction(file));
    }

    for (const templateDto of createTemplateDtos) {
      const document = await QueryFactory.TemplateClient.deferCreate(
        templateDto,
      );

      const documentNode = createProjectDocument(
        String(document.initialId ?? document.id),
        document.name,
        document.id,
      );

      if (newVersionInitialId) {
        folders = replaceItemById(
          String(newVersionInitialId),
          folders as any,
          documentNode as any,
        );
      } else if (folderId) {
        tryInsertInSpecificFolder(folders, folderId, documentNode);
      } else {
        folders.push(documentNode);
      }

      folders = getCopiedItems(folders);
    }

    await QueryFactory.TemplateClient.patchFolders({
      folders,
    });
    await onTemplateCreated?.();
  } catch (e) {
    showErrorToast(e);
  }
}

export async function createReadOnlyDocuments(
  files: DocumentData[],
  revisionId: number,
  onDocumentCreated?: (props: any) => void | Promise<void>,
  onAllDocumentsCreated?: (props: any) => void | Promise<void>,
  folderId?: string,
) {
  const action = (file: DocumentData): CreateDocumentDto => {
    return {
      name: file.name,
      content: file.content,
      status: Status.Draft,
      revisionId: revisionId,
      isReadOnly: true,
      isImported: false,
      folderId: folderId ?? null,
    };
  };

  await createDocuments(
    files,
    revisionId,
    action,
    onDocumentCreated,
    onAllDocumentsCreated,
    folderId,
  );
}

export async function createEditableDocuments(
  files: DocumentData[],
  revisionId: number,
  onDocumentCreated?: (props?: any) => void | Promise<void>,
  onAllDocumentsCreated?: (props?: any) => void | Promise<void>,
  folderId?: string,
) {
  const action = (file: DocumentData): CreateDocumentDto => {
    return {
      name: file.name,
      content: file.content,
      status: Status.Draft,
      revisionId: revisionId,
      isReadOnly: false,
      isImported: true,
      folderId: folderId ?? null,
    };
  };

  await createDocuments(
    files,
    revisionId,
    action,
    onDocumentCreated,
    onAllDocumentsCreated,
    folderId,
  );
}

/**Opens user file dialog window to select importing file. */
export function selectFiles(
  isEditable: boolean = false,
  isMultiple: boolean = false,
) {
  return new Promise<DocumentData[]>((resolve) => {
    const fileSelector = document.createElement('input');
    fileSelector.setAttribute('type', 'file');
    if (isEditable) {
      fileSelector.setAttribute(
        'accept',
        '.dotx,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      );
    }

    if (isMultiple) fileSelector.setAttribute('multiple', 'multiple');
    fileSelector.addEventListener('change', async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files) {
        resolve([]);
        return;
      }

      const readPromises = Array.from(files).map((file) => {
        return new Promise<DocumentData>((resolveFile, _) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolveFile({ name: file.name, content: base64 });
          };

          reader.onerror = (_) => {
            showErrorToast('Unable to open file');
          };
        });
      });

      const addedFiles = await Promise.all(readPromises);
      resolve(addedFiles);
    });
    fileSelector.click();
  });
}

/**Downloads read only document.*/
export const downloadReadOnlyDocument = async (id: number): Promise<void> => {
  const loadDocument = await QueryFactory.DocumentClient.get(id);
  const downloadLink = document.createElement('a');
  downloadLink.href =
    'data:application/octet-stream;base64,' + loadDocument.content;
  downloadLink.download = loadDocument.name;
  downloadLink.click();
};

export const downloadTemplate = async (id: number): Promise<void> => {
  const loadDocument = await QueryFactory.TemplateClient.get(id);
  const exportedDocFile =
    await QueryFactory.DocumentExportClient.deferExportTemplate(
      {
        documentContent: loadDocument.content,
      },
      DocumentExportFormat.Dotx,
    );

  const newBlob = new Blob([exportedDocFile.data], {
    type: exportedDocFile.data.type,
  });

  triggerDownloadFileDialog(
    newBlob,
    loadDocument.name + getFileExtension(DocumentExportFormat.Dotx),
  );
};

export const downloadDocument = async (
  id: number,
  revisionId: number,
  format: DocumentExportFormat,
): Promise<void> => {
  const loadDocument = await QueryFactory.DocumentClient.get(id);
  const docName = await QueryFactory.DocumentExportClient.getExportName(id);
  const exportedDocFile =
    await QueryFactory.DocumentExportClient.deferExportDocument(
      {
        documentContent: loadDocument.content,
        revisionId: revisionId,
        documentId: id,
      },
      format,
    );

  const newBlob = new Blob([exportedDocFile.data], {
    type: exportedDocFile.data.type,
  });

  triggerDownloadFileDialog(newBlob, docName + getFileExtension(format));
};

export function createProjectDocument(
  id: string,
  name: string,
  documentId: number,
): ProjectDocument {
  return {
    id,
    type: NodeType.Document,
    name,
    documentId,
  };
}

const getCopiedItems = (items: TreeItems<BaseNode> | ProjectElement[]) =>
  JSON.parse(JSON.stringify(items));
